import {ref} from 'vue';
import Feature from 'ol/Feature';
import {
    EDrawZoneMapState,
    IDrawZone
} from '@/app/shared/components/kl-draw-zone-map/components/kl-draw-zone-sidebar/kl-draw-zone-sidebar';
import {find, first, forEach, isEmpty, isEqual} from 'lodash-es';
import GeometryImportService from '@/app/shared/components/kl-draw-zone-map/geometry-import-service';
import {AnalyticsService, EAnalyticsCategory, EAnalyticsDrawZoneActions} from '@/app/shared/service/analytics-service';
import WKT from 'ol/format/WKT';
import {useGeometryProjectionStore} from '@/app/shared/components/kl-draw-zone-map/geometry-projection-store';
import {EFeatureToggle, useFeatureToggleStore} from '@/stores/feature-toggle-store';


interface IImportOptions {
    disableReprojectionWarning: boolean;
    allowMultiPolygons: boolean;
    modEnableMultiZones: boolean;
    projection: string;

    getZones: () => IDrawZone[];
    createDrawZone: (feature: Feature) => IDrawZone;
    validateZone: (zone: IDrawZone) => Promise<boolean>;

    handleShowPreview: (features: Feature[]) => void;
    handleRemovePreview: (features: Feature[]) => void;
    handleCancelImport: () => void;
    handleSingleImportDone: (newFeature: Feature, newZone: IDrawZone) => void;
    handleMultiImportDone: (newFeatures: Feature[], newZones: IDrawZone[]) => void;
}

export function useDrawZoneImportContext(options: IImportOptions) {

    const _processBackendValidationInParallel = ref(useFeatureToggleStore().getFeatureToggle(EFeatureToggle.VITE_FEATURE_ENABLE_ZONE_VALIDATION_OPT));

    const importErrors = ref<{ filename: string, errors: string[] }[]>(null);
    const importWarnings = ref<string[]>(null);
    const hasImportErrors = ref(false);
    const hasImportWarnings = ref(false);

    let _importTempContext: { importedFeatures: Feature[], importedZones: IDrawZone[] } = null;
    const showLambertImportWarning = ref(false);

    const _addImportErrors = (errors: { filename: string, errors: string[] }[]) => {
        if (!importErrors.value) {
            importErrors.value = [];
        }
        // TODO: check if filename is already present
        importErrors.value = importErrors.value.concat(errors);
        hasImportErrors.value = true;
    }
    const _addImportWarning = (warning: string) => {
        if (!importWarnings.value) {
            importWarnings.value = [];
        }
        importWarnings.value.push(warning);
        hasImportWarnings.value = true;
    }
    const clearImportErrors = () => {
        importErrors.value = null;
        hasImportErrors.value = false;
        importWarnings.value = null;
        hasImportWarnings.value = false;
    }

    const importFiles = async (files: File[]) => {
        clearImportErrors();

        let errors: { filename: string, errors: string[] }[] = [];
        let importedFeatures: Feature[] = [];
        let importedZones: IDrawZone[] = [];
        let hasAutoTransformedZones: boolean = false;
        for (let i = 0; i < files.length; i++) {
            const file: File = files[i];
            const parsed = await _processFile(file);

            // merge results
            if (!isEmpty(parsed.errors)) {
                errors = errors.concat({filename: file.name, errors: parsed.errors});
            }
            importedFeatures = importedFeatures.concat(parsed.features);
            importedZones = importedZones.concat(parsed.zones);
            hasAutoTransformedZones = hasAutoTransformedZones || parsed.hasAutoTransformedZones;
        }

        // error flow
        if (!isEmpty(errors)) {
            _addImportErrors(errors);
            return;
        }

        // complete import flow
        if (options.disableReprojectionWarning || !hasAutoTransformedZones) {
            _import_step2(importedFeatures, importedZones);
            return;
        }

        // break import flow to show a warning
        _importTempContext = {
            importedFeatures,
            importedZones,
        }
        showLambertImportWarning.value = true;

        options.handleShowPreview(importedFeatures);
    }

    const _processFile = async (file: File): Promise<{
        features: Feature[],
        errors: string[],
        zones: IDrawZone[],
        hasAutoTransformedZones: boolean,
    }> => {

        const result: { features: Feature[], errors: string[], zones: IDrawZone[], hasAutoTransformedZones: boolean } = {
            features: [],
            errors: [],
            zones: [],
            hasAutoTransformedZones: false,
        };

        if (!GeometryImportService.isFileExtensionSupported(file.name)) {
            result.errors.push('Dit bestandstype is niet toegestaan.');
            return result;
        }

        AnalyticsService.SendEvent(EAnalyticsCategory.drawZone, EAnalyticsDrawZoneActions.import, GeometryImportService.getFileExtension(file.name));

        const wkts = await GeometryImportService.import(file, options.allowMultiPolygons);
        if (isEmpty(wkts)) {
            result.errors.push('Bestand bevat geen geldige zones.');
            return result;
        }

        // create features, zones
        // wkts.forEach((wkt: string) => {
        for (let wktIndex = 0; wktIndex < wkts.length; wktIndex++) {
            const wkt = wkts[wktIndex];
            let newFeature = new WKT().readFeature(wkt);

            // Auto convert lambert72 > lambert2008 when necessary (using backend)
            if (options.projection === 'EPSG:3812') {
                if (!useGeometryProjectionStore().isLambert2008(newFeature)) {
                    // console.log('MIGRATION.IMPORT: Auto transform of zone to EPSG:3812');
                    const wkt2008 = await useGeometryProjectionStore().transformWktToLambert2008(wkt);
                    if (!wkt2008) {
                        result.errors.push('Fout bij convertie naar Lambert 2008.');
                    }
                    else {
                        newFeature = new WKT().readFeature(wkt2008);
                        result.hasAutoTransformedZones = true;
                    }
                }
            }

            result.features.push(newFeature);

            const newZone = options.createDrawZone(newFeature);
            result.zones.push(newZone);
        }

        // create validation promises
        const processZone = async (zone: IDrawZone) => {
            if (!await options.validateZone(zone)) {
                forEach(zone.errorMessages, (error: string) => {
                    result.errors.push(error);
                });
            }
        };
        const zoneValidationPromiseFactories = result.zones.map((zone: IDrawZone) => () => new Promise(resolve => resolve(processZone(zone))));

        // parallel or sequential
        if (_processBackendValidationInParallel.value) {
            // TODO: correcte SORT ORDER aanhouden?? (zorgt er enkel voor dat de errors ook in sequentie staan)
            const zoneProcessPromises = zoneValidationPromiseFactories.map(factory => factory());
            await Promise.all(zoneProcessPromises);
        } else {
            for (let i = 0; i < zoneValidationPromiseFactories.length; i++) {
                await zoneValidationPromiseFactories[i]();
            }
        }

        return result;
    }

    const _removeTempImportFeatures = () => {
        if (_importTempContext) {
            options.handleRemovePreview(_importTempContext.importedFeatures);
        }
    }

    const onCancelImport = () => {
        _removeTempImportFeatures();

        _importTempContext = null;
        showLambertImportWarning.value = false;

        clearImportErrors();

        options.handleCancelImport();
    }

    const onContinueImport = () => {
        _removeTempImportFeatures();

        showLambertImportWarning.value = false;
        _import_step2(_importTempContext.importedFeatures, _importTempContext.importedZones);
        _importTempContext = null;
    }

    const _import_step2 = (importedFeatures: Feature[], importedZones: IDrawZone[]) => {

        // check for duplicates
        const filtered = _filterDuplicateZones(importedFeatures, importedZones);

        // the single zone flow
        // navigate to edit mode when:
        // - there was only 1 (new) zone = the single zone flow
        // OR !modEnableMultiZones = the single zone flow
        if (!options.modEnableMultiZones || ((importedFeatures.length === 1) && (filtered.removed === 0))) {
            const newFeature = first(importedFeatures);
            const newZone = first(importedZones);

            options.handleSingleImportDone(newFeature, newZone);

            return;
        }

        // warnings
        if (filtered.removed === 1) {
            _addImportWarning('Er is 1 zone overgeslagen die reeds bestond.');
        }
        if (filtered.removed > 1) {
            _addImportWarning(`Er zijn ${filtered.removed} zones overgeslagen die reeds bestonden.`);
        }

        // happy flow
        options.handleMultiImportDone(filtered.features, filtered.zones);
    }

    const _findZoneWithSameCoordinates = (zoneToFind: IDrawZone, zones: IDrawZone[]): boolean => {
        return !!find(zones, (zone: IDrawZone) => isEqual(zone.coordinates, zoneToFind.coordinates));
    }

    const _filterDuplicateZones = (features: Feature[], newZones: IDrawZone[]): {
        removed: number,
        features: Feature[],
        zones: IDrawZone[]
    } => {
        if (features.length !== newZones.length) {
            throw new Error('argument exception');
        }
        const result = {
            removed: 0,
            features: [],
            zones: []
        };
        for (let i = 0; i < newZones.length; i++) {
            if (_findZoneWithSameCoordinates(newZones[i], options.getZones())) {
                result.removed++;
                continue;
            }
            if (_findZoneWithSameCoordinates(newZones[i], result.zones)) {
                result.removed++;
                continue;
            }
            result.features.push(features[i]);
            result.zones.push(newZones[i]);
        }
        return result;
    }

    return {
        importErrors,
        importWarnings,
        hasImportErrors,
        hasImportWarnings,
        showLambertImportWarning,

        importFiles,
        onCancelImport,
        onContinueImport,
        clearImportErrors,
    }
}
